import { createContext, useContext, type ReactNode } from 'react';
import { Cross } from '@/components/icons';
import { BackButton } from '@/components/ui/back-button';
import { Button } from '@/components/ui/button';
import { DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useDialogChrome } from './dialog-chrome';
import { Heading, Text } from './Typography';

type DialogTone = 'default' | 'success' | 'warning' | 'destructive' | 'info' | 'none';

interface AppDialogAction {
    label: string;
    onClick: () => void;
}

const TONE_STYLES: Record<
    DialogTone,
    {
        icon: string;
    }
> = {
    default: {
        icon: 'border-border bg-surface-muted text-foreground',
    },
    success: {
        icon: 'border-success/20 bg-success/10 text-success',
    },
    warning: {
        icon: 'border-warning/20 bg-warning/12 text-warning',
    },
    destructive: {
        icon: 'border-destructive/20 bg-destructive/10 text-destructive',
    },
    info: {
        icon: 'border-info/20 bg-info/10 text-info',
    },
    // For an icon that brings its own surface — an avatar, a thumbnail. The
    // tinted plate exists to back a bare glyph; behind a self-contained visual
    // it only shows as corners around it. The box itself stays, so the header
    // grid keeps its leading column.
    none: {
        icon: '',
    },
};

/**
 * Tracks whether an ancestor `AppDialogLayout` has already emitted the dialog's
 * accessible `<DialogTitle>`. A modal can nest layouts (e.g. a wizard rendered
 * as `modal-provider` content sits inside the provider's own layout). Radix
 * allows exactly one `DialogTitle` per dialog, so nested layouts read this flag
 * and render a visible-only title instead of a second semantic node. Kept
 * module-private — consumers never need to think about nesting.
 */
const DialogTitleClaimedContext = createContext(false);

interface AppDialogLayoutProps {
    title?: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    footer?: ReactNode;
    tone?: DialogTone;
    className?: string;
    bodyClassName?: string;
    children: ReactNode;
    /** Free-form header content (e.g. a wizard step indicator). */
    header?: ReactNode;
    /** Renders a back control on the leading edge of the header. */
    backAction?: AppDialogAction;
    /** Renders a close (X) control on the trailing edge of the header. */
    closeAction?: AppDialogAction;
}

// Title and description are rendered as separate grid cells (see
// `DialogHeaderRegion`), so each wraps itself in the Radix node when this layout
// owns the dialog's semantics.
function TitleHeading({ title, semantic }: { title: ReactNode; semantic: boolean }) {
    const heading = (
        <Heading as="h2" variant="section">
            {title}
        </Heading>
    );

    return semantic ? <DialogTitle asChild>{heading}</DialogTitle> : heading;
}

function DescriptionText({ description, semantic }: { description: ReactNode; semantic: boolean }) {
    const text = <Text variant="description">{description}</Text>;

    return semantic ? <DialogDescription asChild>{text}</DialogDescription> : text;
}

function CloseButton({ action, className }: { action?: AppDialogAction; className?: string }) {
    if (!action) return null;

    return (
        <Button
            type="button"
            // Mirrors BackButton (ghostBrand, size icon) so the two header
            // controls are identical apart from the glyph.
            variant="ghostBrand"
            size="icon"
            onClick={action.onClick}
            aria-label={action.label}
            title={action.label}
            className={cn('shrink-0', className)}
        >
            <Cross className="size-4" />
        </Button>
    );
}

function DialogHeaderRegion({
    title,
    description,
    icon,
    toneStyle,
    header,
    backAction,
    closeAction,
    semantic,
}: {
    title?: ReactNode;
    description?: ReactNode;
    icon?: ReactNode;
    toneStyle: { icon: string };
    header?: ReactNode;
    backAction?: AppDialogAction;
    closeAction?: AppDialogAction;
    semantic: boolean;
}) {
    const hasSecondRow = description != null || header != null;

    // One header shape for every dialog. The leading control (back or tone icon),
    // the title and the close sit on row 1 of a three-column grid, so `items-center`
    // aligns them on the title's own line — no hand-computed offsets that would
    // drift the moment a font or line-height changes. Anything below the title
    // (description, step indicator) is placed in row 2 of the title's column, which
    // keeps it flush under the title rather than under the back button.
    return (
        <div className="grid grid-cols-[auto_1fr_auto] items-center text-left">
            {backAction ? (
                <BackButton
                    onClick={backAction.onClick}
                    aria-label={backAction.label}
                    title={backAction.label}
                    className="-ml-2 mr-2 shrink-0"
                />
            ) : icon ? (
                <div
                    className={cn(
                        'mr-4 flex size-11 shrink-0 items-center justify-center rounded-2xl',
                        toneStyle.icon,
                    )}
                >
                    {icon}
                </div>
            ) : null}

            <div className="col-start-2 min-w-0">
                {title != null && <TitleHeading title={title} semantic={semantic} />}
            </div>

            <CloseButton action={closeAction} className="col-start-3 -mr-2 ml-2" />

            {hasSecondRow && (
                <div className="col-start-2 row-start-2 min-w-0 space-y-1.5">
                    {description != null && (
                        <DescriptionText description={description} semantic={semantic} />
                    )}
                    {header}
                </div>
            )}
        </div>
    );
}

export default function AppDialogLayout({
    title,
    description,
    icon,
    footer,
    tone = 'default',
    className,
    bodyClassName,
    children,
    header,
    backAction,
    closeAction,
}: AppDialogLayoutProps) {
    const titleClaimed = useContext(DialogTitleClaimedContext);
    const semantic = !titleClaimed;
    const toneStyle = TONE_STYLES[tone];

    // The dialog shell (e.g. `ModalProvider`) hands its close down through
    // context, so whichever layout owns the header renders it in the header row —
    // aligned with the title and the back control. Only the outermost layout takes
    // it (`titleClaimed` marks the nested ones), otherwise a wizard rendering its
    // own layout inside the shell's would show two closes. An explicit prop wins.
    const { close: chromeClose } = useDialogChrome();
    const resolvedCloseAction = closeAction ?? (titleClaimed ? undefined : chromeClose);

    const hasTitleBlock = title != null || description != null;
    const hasHeaderRegion =
        hasTitleBlock || header != null || backAction != null || resolvedCloseAction != null;

    // Radix requires exactly one accessible title per dialog. When this layout
    // owns the semantics but renders no visible title, fall back to an sr-only
    // one (matching the original behavior for chrome-less wizard modals).
    const needsSrOnlyTitle = semantic && title == null;

    return (
        <DialogTitleClaimedContext.Provider value={true}>
            <div className={cn('flex min-h-0 flex-1 flex-col', className)}>
                {hasHeaderRegion && (
                    <div className="shrink-0 px-6 py-5">
                        <DialogHeaderRegion
                            title={title}
                            description={description}
                            icon={icon}
                            toneStyle={toneStyle}
                            header={header}
                            backAction={backAction}
                            closeAction={resolvedCloseAction}
                            semantic={semantic}
                        />
                    </div>
                )}
                {needsSrOnlyTitle && <DialogTitle className="sr-only">Modal</DialogTitle>}
                {/* Marked so the dialog shell can move initial focus here: the close
                    now precedes the body in the DOM, and opening a dialog with the
                    close focused would strand keyboard users in the chrome. */}
                <div
                    data-dialog-body
                    className={cn('min-h-0 flex-1 overflow-y-auto p-6', bodyClassName)}
                >
                    {children}
                </div>
                {footer && (
                    <div className="shrink-0 border-t border-border/70 px-6 py-4">{footer}</div>
                )}
            </div>
        </DialogTitleClaimedContext.Provider>
    );
}
